<template>
  <div class="cf-page-container">
    <div
      v-if="isLoading"
      :class="['cf-container-full-width', { 'cf-page-loader': isLoading }]"
    >
      <div class="cf-loader" />
    </div>

    <template v-else>
      <div class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-20">
        <!-- Back To Browse Menu Button -->
        <button
          v-if="order.orderItemsStaged.length > 0 || order.orderItems.length > 0"
          type="button"
          class="cf-button cf-button--tertiary cf-button--md"
          @click="viewOrderAtTableMenu"
        >
          <span class="cf-icon far fa-angle-left" />
          Browse Menu
        </button>

        <!-- Page Title -->
        <h1 class="cf-title cf-title--1">
          {{ content.PayAtTableOrderSummary.title }}
        </h1>

        <!-- Page Description -->
        <p
          v-if="content.PayAtTableOrderSummary.description"
          class="cf-subtitle cf-subtitle--2"
        >
          {{ content.PayAtTableOrderSummary.description }}
        </p>

        <!-- Empty Cart Section -->
        <div
          v-if="hasNoOrderItems"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-10"
        >
          <h2 class="cf-title cf-title--2">
            Your cart is currently empty.
          </h2>

          <p class="cf-subtitle cf-subtitle--2">
            Browse our menu and start adding some tasty items to your cart.
          </p>

          <!-- Browse Menu Btn -->
          <button
            class="cf-button cf-button--primary cf-button--lg"
            type="button"
            @click="viewOrderAtTableMenu"
          >
            Browse Menu
          </button>

          <!-- Call Table Server Btn -->
          <template v-if="isTableTopDeviceOrdering">
            <cf-title-separator />

            <button
              class="cf-button cf-button--primary cf-button--lg"
              type="button"
              @click="callServerClickHandler"
            >
              Call Server
            </button>
          </template>
        </div>

        <!-- Multiple Orders Selection CTA -->
        <div
          v-if="hasMultipleOrdersForTable"
          class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-20"
        >
          <h3 class="cf-title cf-title--3">
            Looks like there are multiple orders open for this table.
          </h3>

          <p class="cf-subtitle cf-subtitle--2">
            You can select a different order to view, modify or pay for.
          </p>

          <button
            v-if="hasMultipleOrdersForTable"
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            type="button"
            @click="viewOpenOrdersClickHandler"
          >
            Select Order
          </button>
        </div>

        <!-- Staged Items Warning -->
        <div
          v-if="order.orderItemsStaged.length > 0"
          class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-alt-background cf-margin-top-16"
        >
          <span class="cf-icon cf-icon--red far fa-exclamation-circle" />

          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            You have pending order items. Please send your items to the kitchen to continue with your order.
          </p>
        </div>

        <!-- Table Top Device QR Code -->
        <div
          v-if="order.orderItemsStaged.length === 0 && order.orderItems.length > 0 && isTableTopDeviceOrdering"
          class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-16"
        >
          <div class="cf-qr-code-container">
            <vue-qrcode
              v-if="qrCode"
              class="cf-image cf-image--auto-w cf-image--border"
              :value="qrCode"
              :scale="5"
            />
          </div>

          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-text-align-center">
            Simply scan the QR code above on your phone to begin payment.
          </p>
        </div>

        <!-- Find Order Cta/Form -->
        <cf-validated-form
          v-if="hasNoOrderItems && !isTableTopDeviceOrdering"
          name="emptyCart"
          @valid-submit="findOrder"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-card cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-10">
              <!-- Table Number Display --->
              <template v-if="tableNumberDisplay && !isFindingOrder">
                <h3 class="cf-title cf-title--3">
                  Your {{ $lowercase(userLocationType.description) }} number is:
                </h3>

                <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
                  {{ tableNumberDisplay }}
                </p>
              </template>

              <!-- Find Order Cta -->
              <template v-else>
                <h2 class="cf-title cf-title--2">
                  Already have an open order?
                </h2>

                <p class="cf-subtitle cf-subtitle--2">
                  Enter your {{ $lowercase(userLocationType.description) }} number to load your order:
                </p>

                <cf-text-input
                  :model-value="manuallyEnteredTableNumber"
                  type="text"
                  auto-width
                  :label="`${userLocationType.description} #`"
                  :icon-class="userLocationType.icon || 'fa-utensils'"
                  name="manuallyEnteredTableNumber"
                  :validations="{
                    required: true,
                    alpha_num: true,
                  }"
                  @update:model-value="handleTableNumberInput"
                />

                <button
                  :class="['cf-button cf-button--tertiary cf-button--lg', { 'cf-button--loading': isFindingOrder }]"
                  type="submit"
                >
                  Find Order
                </button>
              </template>
            </div>
          </div>
        </cf-validated-form>

        <!-- Send Items to Kitchen/Existing Items -->
        <div
          v-else
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <template v-if="order.orderItemsStaged.length > 0">
            <h3 class="cf-title cf-title--3">
              Your Order
            </h3>

            <!-- Order items -->
            <cf-order-items
              :order="order"
              :order-items="order.orderItemsStaged"
              :is-read-only="isSubmittingOrder"
            />

            <!-- Upsell Items carousel -->
            <cf-upsell-order-items v-if="isUpsellDisplayed" />

            <cf-validated-form
              name="cart"
              @valid-submit="submitOrder"
            >
              <div class="cf-card cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-10">
                <!-- Table Number Input -->
                <template v-if="!tableNumberDisplay">
                  <h3 class="cf-title cf-title--3">
                    Enter your {{ $lowercase(userLocationType.description) }} number below:
                  </h3>

                  <cf-text-input
                    :model-value="manuallyEnteredTableNumber"
                    type="text"
                    auto-width
                    :label="`${userLocationType.description} #`"
                    class="auto-spacing-left auto-spacing-right"
                    :icon-class="userLocationType.icon || 'fa-utensils'"
                    name="manuallyEnteredTableNumber"
                    :validations="{
                      required: true,
                      alpha_num: true,
                    }"
                    @update:model-value="handleTableNumberInput"
                  />
                </template>

                <div class="cf-flex cf-flex--column">
                  <!-- Open Tab Payment Method Selection -->
                  <cf-payment-methods
                    v-if="supportsOpenTab && order.orderItemCount === 0"
                    primary-cta-text="Send Order To Kitchen"
                    :pay-cta-text="content.OpenTabPaymentDetails.metadata.cta"
                    :payment-modal-title="content.OpenTabPaymentDetails.title"
                    :hidden-elements="{
                      contactInfo: true,
                      total: true,
                      signIn: true
                    }"
                    is-pre-auth
                    :total="tabHoldAmount"
                    :checkout-handler="submitPaymentInfo"
                  >
                    <template #payment-modal-top>
                      <p
                        v-if="content.OpenTabPaymentDetails.metadata.subtitle.includes('{{defaultOpenTabAmount}}')"
                        class="cf-subtitle cf-subtitle--2"
                        v-html="openTabDefaultSubtitleHtml"
                      />

                      <p
                        v-else
                        class="cf-subtitle cf-subtitle--2"
                      >
                        {{ content.OpenTabPaymentDetails.metadata.subtitle }}
                      </p>
                    </template>
                  </cf-payment-methods>

                  <!-- Send Order to Kitchen -->
                  <button
                    v-else
                    :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': isSubmittingOrder }]"
                    type="submit"
                  >
                    {{ sendToKitchenCta }}
                  </button>
                </div>
              </div>
            </cf-validated-form>
          </template>

          <!-- Go To Pay-at-Table Checkout Btn -->
          <button
            v-else-if="order.orderItems.length > 0 && !isTableTopDeviceOrdering"
            class="cf-button cf-button--primary cf-button--lg cf-button--fill"
            type="button"
            @click="goToCheckoutPage"
          >
            Checkout
          </button>
        </div>

        <!-- Expandable section that shows order items -->
        <cf-expandable-section
          v-if="order.orderItems.length > 0"
          :auto-expand="true"
        >
          <template #title>
            <h3 class="cf-title cf-title--3">
              Existing Cart
            </h3>
          </template>

          <template #content>
            <cf-order-items
              :order="order"
              :is-read-only="true"
            />
          </template>
        </cf-expandable-section>
      </div>

      <!-- Order Totals -->
      <div
        v-if="order.orderItems.length > 0"
        class="cf-card cf-card--mobile cf-background-white cf-sticky-position cf-sticky-position--bottom-position"
      >
        <cf-order-totals
          :order="order"
          :totals="{ totalWithTip: totalWithTipAndCharges }"
        />
      </div>
    </template>
  </div>
</template>


<script>
  import { mapGetters, mapState } from 'vuex';

  import formatUsd from 'format-usd';

  import IframeEventService from '@/services/iframe-event';
  import errorHandlers from '@/services/error-handlers';
  import pay from '@/services/pay';
  import events from '@/services/events';

  import lowercase from '@/filters/lowercase';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfExpandableSection from '@/view/components/shared/cf-expandable-section/index.vue';

  import CfOrderTotals from '@/view/components/globals/cf-order-totals/index.vue';
  import CfUpsellOrderItems from '@/view/components/globals/cf-upsell-order-items/index.vue';
  import CfOrderItems from '@/view/components/globals/cf-order-items/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';


  export default {
    name: 'OrderAtTableOrderSummaryPage',

    components: {
      CfValidatedForm,
      CfTextInput,
      CfOrderItems,
      CfExpandableSection,
      CfPaymentMethods,
      CfUpsellOrderItems,
      CfTitleSeparator,
      CfOrderTotals
    },

    data() {
      return {
        isLoading: false,
        isSubmittingOrder: false,
        manuallyEnteredTableNumber: null,
        isFindingOrder: false,
        noExistingOrderFound: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('orders', ['order', 'tableNumberDisplay', 'userLocationType', 'upsellItems', 'orders']),
      ...mapGetters('session', ['session', 'isTableTopDeviceOrdering']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('socket', ['orderRoom']),

      ...mapState('orders', {
        stateOrder: 'order'
      }),

      isUpsellDisplayed() {
        return this.upsellItems.checkoutUpsellItems.length > 0;
      },

      tabHoldAmount() {
        return this.selectedLocation.defaultTabAmount;
      },

      hasNoOrderItems() {
        return this.order.orderItems.length === 0 && this.order.orderItemsStaged.length === 0;
      },

      hasMultipleOrdersForTable() {
        /** Note:
         * Limit order selection to table top devices for now.
         */
        return this.orders.length > 1 && this.isTableTopDeviceOrdering;
      },

      qrCode() {
        const url = this.$router.resolve({
          name: 'payAtTableCheckout',
          params: {
            merchantId: this.$route.params.merchantId
          },
          query: {
            ml: this.session.route.merchantLocationId,
            oi: this.order.orderId,
            tb: this.session.route.tableNumber
          }
        });

        const windowHost = window.location.host;

        const qrCode = `${windowHost}${url.href}`;

        return qrCode;
      },

      openTabDefaultSubtitleHtml() {
        const holdAmount = this.order.subtotalIncludingStagedItems > this.tabHoldAmount
          ? this.order.subtotalIncludingStagedItems
          : this.tabHoldAmount;

        return this.content.OpenTabPaymentDetails.metadata.subtitle.replace(
          '{{defaultOpenTabAmount}}',
          `<span class="bold">${formatUsd({ amount: holdAmount })}</span>`
        );
      },

      supportsOpenTab() {
        // "OpenTab" feature refers to merchant setting while "isOpenTabEnabled" is a FE derived property determined by the payment provider
        // See: parse-location helper.
        return this.selectedLocation.features.OpenTab && this.selectedLocation.isOpenTabEnabled
          && (
            !this.order.orderId /* no order exists yet because no table number has been specified */
            || this.order.features?.includes('OpenTab') /* an order does exist and it supports OpenTab */
          );
      },

      totalWithTipAndCharges() {
        return Math.round(this.order.totalWithTipAndCharges * 100) / 100;
      },

      sendToKitchenCta() {
        return this.content.OrderAtTableOrderSummary.metadata.sendOrderCta;
      }
    },

    watch: {
      'orderRoom.status': {
        handler: 'orderRoomStatusChangeHandler',
        immediate: true
      }
    },

    created() {
      if (this.isTableTopDeviceOrdering) {
        /** Need to re-init to get latest order and/or orders data that was potentially modified on the POS */
        this.initializeOrderAtTable();
      }
    },

    methods: {
      async initializeOrderAtTable() {
        try {
          this.isLoading = true;

          await this.$store.dispatch('orderAtTable/initializeOrderAtTable');
        }
        catch (error) {
          errorHandlers.silent(error);
        }
        finally {
          this.isLoading = false;
        }
      },

      orderRoomStatusChangeHandler(status) {
        if (status === 'closed') {
          IframeEventService.triggerOrderPaid();

          this.viewOrderAtTableMenu();
        }

        if (status === 'locked' && this.isTableTopDeviceOrdering) {
          this.$router.push({
            name: 'orderAtTableCartSummaryPage',
            params: { merchantId: this.$route.params.merchantId },
            query: { tb: this.session.route.tableNumber, ttd: 1 }
          });
        }
      },

      handleTableNumberInput(value) {
        this.manuallyEnteredTableNumber = value.toUpperCase();
      },

      viewOpenOrdersClickHandler() {
        this.$openModal('select-table-top-device-order-modal', { orders: this.orders });
      },

      viewOrderAtTableMenu() {
        this.$router.push({
          name: 'orderAtTableMenu',
          params: {
            merchantId: this.$route.params.merchantId,
            ml: this.session.route.merchantLocationId
          }
        });
      },

      callServerClickHandler() {
        IframeEventService.triggerCallTableServer();
      },

      async submitOrder() {
        const redirectToMenuPage = this.merchant.features['order-at-table-redirect-to-checkout-disabled'];

        try {
          this.isSubmittingOrder = true;

          if (this.manuallyEnteredTableNumber) {
            this.$store.dispatch('session/updateRouteParamsDirectly', {
              tableNumber: this.manuallyEnteredTableNumber
            });
          }

          // check to see if the order contains any items yet before sending order to kitchen
          const isFirstOrderSubmit = !this.order.orderItems.length;

          await this.$store.dispatch('orders/sendStagedItemsToKitchen');

          // this.$triggerGoogleTrackingEvent('SendOrderToKitchen', 'Order');

          this.$triggerNewRelicEvent('SendOrderToKitchen', 'Information', 'Sent Order To Kitchen');

          if (this.supportsOpenTab) {
            if (isFirstOrderSubmit) {
              // prompt user to checkout now (close tab) or leave tab open
              this.$openModal('openTabFinishOrdering');
            }
            else {
              this.viewOrderAtTableMenu();
            }
          }
          else if (redirectToMenuPage) {
            this.viewOrderAtTableMenu();
          }
          else if (!this.isTableTopDeviceOrdering) {
            this.goToCheckoutPage();
          }
        }

        catch (error) {
          this.isSubmittingOrder = false;

          errorHandlers.generic(error);

          /*
          * This code was causing an issue where if something errored out while a user
          * was trying to send something to the kitchen, their session would lose
          * the context of what table they were. We are commenting this code out
          * for now and will revisit this if it becomes a problem - Finney 3/5/26
          */
          // this.$store.dispatch('session/updateRouteParamsDirectly', {
          //   tableNumber: null
          // });
        }
      },

      async findOrder({ checkoutNow = true } = {}) {
        try {
          this.isFindingOrder = true;

          this.$store.dispatch('session/updateRouteParamsDirectly', {
            tableNumber: this.manuallyEnteredTableNumber
          });

          await this.$store.dispatch('orderAtTable/getTableOrder', { silent: true, throw: true });

          if (this.stateOrder.orderItems.length > 0) {
            if (checkoutNow) this.goToCheckoutPage();
          }

          else {
            const message = `There are no open orders for your ${lowercase(this.userLocationType.description)}`;

            events.emit('notification', {
              type: 'warning',
              message
            });
          }
        }

        catch (error) {
          errorHandlers.generic(error);

          this.$store.dispatch('session/updateRouteParamsDirectly', {
            tableNumber: null
          });
        }

        finally {
          this.isFindingOrder = false;
        }
      },

      async submitPaymentInfo({ contactInfo, paymentMethod }) {
        // set manually entered table number and fetch/begin order at that table
        if (this.manuallyEnteredTableNumber) {
          await this.findOrder({ checkoutNow: false });
        }

        if (this.order.payments.length) {
          events.emit('notification', {
            type: 'error',
            message: `A tab has already been started at this ${this.userLocationType.description.toLowerCase()}`,
            clickToDismiss: true
          });
        }
        else {
          // authorize the payment method with a hold
          await pay.addPayment({
            contactInfo,
            paymentMethod,
            order: this.stateOrder,
            total: this.tabHoldAmount,
            completePayment: false // Don't complete the payment yet since this //
            // is just a hold. The payment will be updated with the final order //
            // amounts (total, tip, tax) and completed once the tab is closed   //
          });

          // send the items to the kitchen
          await this.submitOrder();
        }
      },

      goToCheckoutPage() {
        // Getter was not updating properly, so reference state directly.
        const orderId = this.order.orderId || this.stateOrder.orderId;

        this.$router.push({
          name: 'payAtTableCheckout',
          params: {
            merchantId: this.$route.params.merchantId
          },
          query: {
            ml: this.session.route.merchantLocationId,
            oi: orderId,
            tb: this.session.route.tableNumber
          }
        });
      }
    }
  };
</script>
